<template>
  <section class="batting-section" :aria-label="`${teamName} Projected Batting`">
    <div class="batting-section__inner">
      <div class="section__header">
        <div class="section-number">{{ sectionNumber }}</div>
        <div class="section__titles">
          <p class="eyebrow">Projected Batting Line &mdash; {{ teamLabel }}</p>
          <h2 class="display-lg">{{ teamName }} <em>lineup.</em></h2>
        </div>
      </div>

      <hr class="rule rule--double" />

      <article class="paper-card batting-card">
        <div class="card__scroll">
          <table class="batting-table">
            <thead>
              <tr>
                <th scope="col" class="th--order">#</th>
                <th scope="col" class="th--player">Player</th>
                <th scope="col" class="th--pos">Pos</th>
                <th scope="col" class="th--num">H</th>
                <th scope="col" class="th--num">HR</th>
                <th scope="col" class="th--num">RBI</th>
                <th scope="col" class="th--num">R</th>
                <th scope="col" class="th--num">K</th>
                <th scope="col" class="th--num">BB</th>
                <th scope="col" class="th--chevron" aria-hidden="true"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="player in players"
                :key="player.player_id"
                class="batting-row"
                :class="{ 'batting-row--active': activePlayerId === player.player_id }"
                @click="toggle(player)"
              >
                <td class="td--order numeric">{{ player.batting_order ?? '—' }}</td>
                <td class="td--player">
                  <span class="player-name">{{ player.name }}</span>
                  <span v-if="edgesFor(player.player_id).length" class="prop-chips" @click.stop>
                    <span
                      v-for="pe in edgesFor(player.player_id)"
                      :key="`${pe.market}-${pe.line}`"
                      class="prop-chip"
                      :class="edgeClass(pe.edge)"
                      :title="`${propMarketLabel(pe.market)} ${pe.line}: sim ${formatPct(
                        pe.sim_over_pct
                      )} vs implied ${formatPct(pe.implied_over_pct)}`"
                    >
                      {{ propMarketShort(pe.market) }} {{ pe.line }}
                      {{ formatEdge(pe.edge) }}
                    </span>
                  </span>
                </td>
                <td class="td--pos">{{ player.position ?? '—' }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_hits) }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_hr) }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_rbi) }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_runs) }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_k) }}</td>
                <td class="td--num numeric">{{ fmt(player.mean_bb) }}</td>
                <td class="td--chevron" aria-hidden="true">
                  <span
                    class="chevron"
                    :class="{ 'chevron--open': activePlayerId === player.player_id }"
                    >›</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PlayerProjectionResponse, PropEdge } from '../../api/games'
import {
  edgeClass,
  formatEdge,
  formatPct,
  propMarketLabel,
  propMarketShort
} from '../../utils/odds'

const props = defineProps<{
  players: PlayerProjectionResponse[]
  teamName: string
  teamLabel: string
  sectionNumber: string
  activePlayerId: number | null
  propEdges?: PropEdge[]
}>()

const emit = defineEmits<{
  select: [player: PlayerProjectionResponse | null]
}>()

function toggle(player: PlayerProjectionResponse) {
  emit('select', props.activePlayerId === player.player_id ? null : player)
}

function edgesFor(playerId: number): PropEdge[] {
  return (props.propEdges ?? []).filter(p => p.player_id === playerId)
}

function fmt(val: number | undefined | null): string {
  if (val === undefined || val === null || Number.isNaN(val)) return '—'
  return val.toFixed(2)
}
</script>

<style>
@import '../../assets/projected-table.css';

/* Batting-specific overrides on top of shared projected-table.css. */
.batting-table {
  min-width: 560px;
}

.batting-table .th--order {
  width: 2rem;
}

.batting-table .th--pos {
  width: 3rem;
}

.batting-table .td--order {
  color: var(--ink-ghost);
  font-size: 0.78rem;
}

.batting-table .td--pos {
  font-family: var(--font-ui);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--ink-medium);
}
</style>
